/**
 * Turns a failed 2FA request into a translation key.
 *
 * The error interceptor reshapes every HTTP error into
 * { status, message, errors, originalError }. The backend puts a fixed
 * English string into `message` for each known failure, so matching that
 * string exactly is the contract. Nothing from the backend is ever shown
 * raw: unknown errors fall back to the generic key the caller passes in.
 */

/** What the error interceptor hands to subscribers */
export interface InterceptedHttpError {
  status?: number;
  message?: string;
  /** 422 validation errors arrive as { field: ['message', ...] } */
  errors?: Record<string, string[]> | unknown[];
}

export type TwoFactorErrorKind =
  | 'session_required'
  | 'impersonation_not_allowed'
  | 'invalid_password'
  | 'invalid_code'
  | 'setup_expired'
  | 'setup_not_started'
  | 'method_not_found'
  | 'not_enabled'
  | 'validation'
  | 'unknown';

export interface MappedTwoFactorError {
  kind: TwoFactorErrorKind;
  translationKey: string;
}

/** Backend messages matched exactly, in the backend's own wording */
export const TWO_FACTOR_BACKEND_MESSAGES = {
  SESSION_REQUIRED: 'two_factor_session_required',
  IMPERSONATION_NOT_ALLOWED: 'impersonation_not_allowed',
  INVALID_PASSWORD: 'Invalid password',
  INVALID_CODE: 'Invalid verification code',
  SETUP_EXPIRED: '2FA setup expired, generate a new QR code',
  SETUP_NOT_STARTED: '2FA secret not generated',
  METHOD_NOT_FOUND: 'Authenticator not found',
  NOT_ENABLED: '2FA is not enabled',
} as const;

const ERROR_KEY_PREFIX = 'profile.authenticators.errors';

/** The kinds the backend names with a fixed message (everything but validation and unknown) */
type BackendMessageKind = Exclude<TwoFactorErrorKind, 'validation' | 'unknown'>;

const MESSAGE_TO_KIND: Record<string, BackendMessageKind> = {
  [TWO_FACTOR_BACKEND_MESSAGES.SESSION_REQUIRED]: 'session_required',
  [TWO_FACTOR_BACKEND_MESSAGES.IMPERSONATION_NOT_ALLOWED]: 'impersonation_not_allowed',
  [TWO_FACTOR_BACKEND_MESSAGES.INVALID_PASSWORD]: 'invalid_password',
  [TWO_FACTOR_BACKEND_MESSAGES.INVALID_CODE]: 'invalid_code',
  [TWO_FACTOR_BACKEND_MESSAGES.SETUP_EXPIRED]: 'setup_expired',
  [TWO_FACTOR_BACKEND_MESSAGES.SETUP_NOT_STARTED]: 'setup_not_started',
  [TWO_FACTOR_BACKEND_MESSAGES.METHOD_NOT_FOUND]: 'method_not_found',
  [TWO_FACTOR_BACKEND_MESSAGES.NOT_ENABLED]: 'not_enabled',
};

const KIND_TO_KEY: Record<BackendMessageKind, string> = {
  session_required: `${ERROR_KEY_PREFIX}.session_required`,
  impersonation_not_allowed: `${ERROR_KEY_PREFIX}.impersonation_not_allowed`,
  invalid_password: `${ERROR_KEY_PREFIX}.invalid_password`,
  invalid_code: `${ERROR_KEY_PREFIX}.invalid_code`,
  setup_expired: `${ERROR_KEY_PREFIX}.setup_expired`,
  setup_not_started: `${ERROR_KEY_PREFIX}.setup_not_started`,
  method_not_found: `${ERROR_KEY_PREFIX}.method_not_found`,
  not_enabled: `${ERROR_KEY_PREFIX}.not_enabled`,
};

/**
 * Laravel validation failures (422 with an `errors` object) name the field
 * that was rejected. Each field we send has its own message.
 */
const VALIDATION_FIELD_TO_KEY: Record<string, string> = {
  password: `${ERROR_KEY_PREFIX}.password_required`,
  label: `${ERROR_KEY_PREFIX}.label_invalid`,
  code: `${ERROR_KEY_PREFIX}.invalid_code`,
};

/**
 * @param error        whatever the subscribe error callback received
 * @param fallbackKey  translation key for anything the contract does not name
 */
export function mapTwoFactorError(error: unknown, fallbackKey: string): MappedTwoFactorError {
  const intercepted = toInterceptedError(error);

  const kindFromMessage = intercepted.message ? MESSAGE_TO_KIND[intercepted.message] : undefined;
  if (kindFromMessage) {
    return { kind: kindFromMessage, translationKey: KIND_TO_KEY[kindFromMessage] };
  }

  const validationKey = findValidationKey(intercepted);
  if (validationKey) {
    return { kind: 'validation', translationKey: validationKey };
  }

  return { kind: 'unknown', translationKey: fallbackKey };
}

function toInterceptedError(error: unknown): InterceptedHttpError {
  if (typeof error !== 'object' || error === null) {
    return {};
  }
  return error as InterceptedHttpError;
}

function findValidationKey(error: InterceptedHttpError): string | null {
  if (error.status !== 422 || !isFieldErrorObject(error.errors)) {
    return null;
  }
  const rejectedField = Object.keys(error.errors).find((field) => field in VALIDATION_FIELD_TO_KEY);
  return rejectedField ? VALIDATION_FIELD_TO_KEY[rejectedField] : null;
}

function isFieldErrorObject(errors: unknown): errors is Record<string, string[]> {
  return typeof errors === 'object' && errors !== null && !Array.isArray(errors);
}
